import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';

import { CompletionStatus } from '@eika-felles/kyc-declaration-lib/dist/generated-types';

import {
    FormTypeApplicationCoApplicant,
    FormTypeApplicationMainApplicant,
    getOrCreateSblConsent,
    kycError,
    loanApplicationMainApplicantKycNextEvent,
    loanApplicationMainApplicantNextEvent,
    loanApplicationSecondApplicantNextEvent,
    loanApplicationSecondApplicantSkipNextEvent,
    openStep,
    sblFailed,
    setKycAnswered,
    toggleCoApplicant,
} from '../../actions';
import { fetchCalculatorCache } from '../../actions/calculatorCache';
import { DispatchWithState, StringKeyAnyValue, StringKeyValue } from '../../actions/types';
import ExpandableTableItems from '../../components/LoanApplication/ExpandableTableItems';
import ProductImage from '../../components/ProductImage/ProductImage';
import Step from '../../components/Steps/Step';
import StepCounter from '../../components/Steps/StepCounter';
import Steps from '../../components/Steps/Steps';
import { AppState } from '../../reducers';
import { ApplicationReducer } from '../../reducers/application';
import { CalculatorReducer } from '../../reducers/calculator';
import { LoaderReducer } from '../../reducers/loader';
import { SblReducer } from '../../reducers/sbl';
import { StepsReducer } from '../../reducers/steps';
import { UserReducer } from '../../reducers/user';
import { getProductApplicationTitle, getProductIcon } from '../../util/mappings';
import LoanApplicantMain from './LoanApplicantMain/LoanApplicantMain';
import LoanApplicantMainKyc from './LoanApplicantMainKyc/LoanApplicantMainKyc';
import LoanApplicantSecond from './LoanApplicantSecond/LoanApplicantSecond';
import LoanApplicationSbl from './LoanApplicationSbl/LoanApplicationSbl';
import LoanInfo from './LoanInfo/LoanInfo';

interface Props {
    action: {
        fetchCalculatorCache: () => DispatchWithState;
        loanApplicationMainApplicantNextEvent: (form: FormTypeApplicationMainApplicant) => DispatchWithState;
        loanApplicationMainApplicantKycNextEvent: () => DispatchWithState;
        loanApplicationSecondApplicantNextEvent: (form: FormTypeApplicationCoApplicant) => DispatchWithState;
        loanApplicationSecondApplicantSkipNextEvent: () => DispatchWithState;
        getOrCreateSblConsent: () => DispatchWithState;
        toggleCoApplicant: (hasCoApplicant: boolean) => void;
        openStep: (destinationStep: number) => void;
        setKycAnswered: () => void;
        kycError: () => void;
        sblFailed: () => void;
    };
    application: ApplicationReducer;
    calculator: CalculatorReducer;
    sbl: SblReducer;
    form: StringKeyAnyValue;
    loader: LoaderReducer;
    text: StringKeyValue;
    steps: StepsReducer;
    user: UserReducer;
}

class LoanApplication extends Component<Props> {
    render() {
        const { action, application, calculator, steps, text, form, loader, user, sbl } = this.props;
        const { openStep, loanApplicationMainApplicantKycNextEvent, setKycAnswered, kycError } = action;
        const { loanInfo } = calculator;
        const { loanType, loanProduct } = loanInfo;
        const step = steps?.steps;
        const applicationTitle = getProductApplicationTitle(loanType, loanProduct, text);

        return (
            <div className="loan-application">
                <div style={{ maxWidth: '500px', margin: '0 auto' }}>
                    <>
                        {step[0].open ? (
                            <ProductImage
                                className="loan-application__intro"
                                image={getProductIcon(loanType, loanProduct)}
                                title={applicationTitle}
                            />
                        ) : (
                            <h1 className="text-center">{applicationTitle}</h1>
                        )}

                        {step && (
                            <Steps>
                                <Step open={step[0].open}>
                                    <StepCounter currentStep="1" totalSteps="5" />
                                    <LoanInfo action={action} calculator={calculator} loader={loader} text={text} />
                                </Step>
                                <Step open={step[1].open}>
                                    <StepCounter currentStep="2" totalSteps="5" />
                                    <LoanApplicationSbl action={action} sbl={sbl} loader={loader} text={text} />
                                </Step>
                                <Step open={step[2].open}>
                                    <StepCounter currentStep="3" totalSteps="5" />
                                    <LoanApplicantMain
                                        action={action}
                                        application={application}
                                        calculator={calculator}
                                        loader={loader}
                                        form={form}
                                        text={text}
                                    />
                                </Step>
                                <Step open={step[3].open}>
                                    <StepCounter currentStep="4" totalSteps="5" />
                                    <LoanApplicantMainKyc
                                        ssn={user.context.ssn}
                                        previousEvent={() => openStep(3)}
                                        completeEvent={(status: string) => {
                                            if (status === CompletionStatus.FAILED) kycError();
                                            else if (
                                                status === CompletionStatus.OK ||
                                                status === CompletionStatus.REQUIRES_MANUAL_CONTROL_BY_ADVISOR
                                            ) {
                                                setKycAnswered();
                                                loanApplicationMainApplicantKycNextEvent();
                                            }
                                        }}
                                        text={text}
                                        loader={loader}
                                    />
                                </Step>

                                <Step open={step[4].open}>
                                    <StepCounter currentStep="4" totalSteps="5" />
                                    <LoanApplicantSecond
                                        action={action}
                                        application={application}
                                        loader={loader}
                                        calculator={calculator}
                                        form={form}
                                        text={text}
                                    />
                                </Step>
                            </Steps>
                        )}

                        <ExpandableTableItems text={text} />
                    </>
                </div>
            </div>
        );
    }
}

const mapStateToProps = (state: AppState) => ({
    text: state.context.config.data.text,
    form: state.context.config.data.form,
    application: state.application,
    calculator: state.calculator,
    loader: state.loader,
    sbl: state.sbl,
    steps: state.steps,
    user: state.user,
    kyc: state.kyc,
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    action: bindActionCreators(
        {
            loanApplicationMainApplicantNextEvent,
            loanApplicationMainApplicantKycNextEvent,
            loanApplicationSecondApplicantNextEvent,
            loanApplicationSecondApplicantSkipNextEvent,
            toggleCoApplicant,
            fetchCalculatorCache,
            getOrCreateSblConsent,
            openStep,
            setKycAnswered,
            kycError,
            sblFailed,
        },
        dispatch,
    ),
});

export default connect(mapStateToProps, mapDispatchToProps)(LoanApplication);
